import { FootprintPolygonsType, GridSpaceType, PositionType } from 'data/types';
import { getSpacePolygonVerticesFeet } from 'utils/3d-polygon';
import {
  ExteriorWall3DAttachmentType,
  Wall3DAttachmentType,
  Wall3DFacingType
} from 'utils/3d-wall-placement-types';
import { nearestAttachedSegment } from 'utils/equipment-rotation';
import { isPointInPolygon } from 'utils/footprint';
import {
  buildWallSegments,
  nearestSegment,
  projectPointOnWallSegment,
  signedDistanceToSegmentLine
} from 'utils/wall-segment';
import { WallSegmentType } from 'utils/wall-segment-types';
import { exteriorSnapSegments } from 'utils/wall-snap';
import { SnapPolygonType } from 'utils/wall-snap-types';

export function gridSpaceFeetPolygon(
  space: Pick<GridSpaceType, 'position' | 'vertices'>
): PositionType[] {
  const relative = getSpacePolygonVerticesFeet(
    space.vertices,
    space.position.width,
    space.position.height
  );
  return relative.map((vertex) => ({
    x: vertex.x + space.position.x,
    y: vertex.y + space.position.y
  }));
}

function realSpaces(spaces: GridSpaceType[]): GridSpaceType[] {
  return spaces.filter((space) => space.type === 'space');
}

export function realSpacePolygons(spaces: GridSpaceType[]): PositionType[][] {
  return realSpaces(spaces).map((space) => gridSpaceFeetPolygon(space));
}

function realSpaceSnapPolygons(spaces: GridSpaceType[]): SnapPolygonType[] {
  return realSpaces(spaces).map((space) => ({
    id: space.id,
    vertices: gridSpaceFeetPolygon(space)
  }));
}

function attachmentForSegment(
  segment: WallSegmentType,
  center: PositionType
): Wall3DAttachmentType {
  return {
    segment,
    projection: projectPointOnWallSegment(segment, center),
    signedDistanceFt: signedDistanceToSegmentLine(segment, center)
  };
}

function attachmentOnSegments(
  center: PositionType,
  segments: WallSegmentType[]
): Wall3DAttachmentType | null {
  const nearest = nearestSegment(segments, center);
  return nearest ? attachmentForSegment(nearest.segment, center) : null;
}

export function nearestWallAttachment(
  center: PositionType,
  spaces: GridSpaceType[]
): Wall3DAttachmentType | null {
  const segments = realSpacePolygons(spaces).flatMap((polygon) =>
    buildWallSegments(polygon, null)
  );
  return attachmentOnSegments(center, segments);
}

export function containingSpaceWallAttachment(
  center: PositionType,
  spaces: GridSpaceType[]
): Wall3DAttachmentType | null {
  const containing = realSpacePolygons(spaces).find((polygon) =>
    isPointInPolygon(center, polygon)
  );
  if (!containing) {
    return null;
  }
  return attachmentOnSegments(center, buildWallSegments(containing, null));
}

export function exteriorWallAttachment(
  center: PositionType,
  sizeFt: number,
  spaces: GridSpaceType[],
  footprints: FootprintPolygonsType
): ExteriorWall3DAttachmentType | null {
  const segments = exteriorSnapSegments(realSpaceSnapPolygons(spaces), {
    footprints
  });
  const attached = nearestAttachedSegment(center, sizeFt, segments, 'outside');
  if (attached) {
    return { ...attachmentForSegment(attached, center), attached: true };
  }
  const nearest = attachmentOnSegments(center, segments);
  return nearest ? { ...nearest, attached: false } : null;
}

export function wallMountedYaw(outwardNormal: PositionType): number {
  return Math.atan2(-outwardNormal.x, -outwardNormal.y);
}

export function wallFacingYaw(outwardNormal: PositionType): number {
  return Math.atan2(-outwardNormal.y, outwardNormal.x);
}

export function moveInward(
  point: PositionType,
  outwardNormal: PositionType,
  distance: number
): PositionType {
  return {
    x: point.x - outwardNormal.x * distance,
    y: point.y - outwardNormal.y * distance
  };
}

export function nearestWallFacing(
  center: PositionType,
  insetFt: number,
  spaces: GridSpaceType[]
): Wall3DFacingType {
  const attachment = nearestWallAttachment(center, spaces);
  if (!attachment) {
    return { point: center, normal: null };
  }
  const normal = attachment.segment.outwardNormal;
  return { point: moveInward(center, normal, insetFt), normal };
}

export function flushExteriorWallFacing(
  center: PositionType,
  sizeFt: number,
  flushDistanceFt: number,
  spaces: GridSpaceType[],
  footprints: FootprintPolygonsType
): Wall3DFacingType {
  const attachment = exteriorWallAttachment(center, sizeFt, spaces, footprints);
  if (!attachment) {
    return { point: center, normal: null };
  }
  const normal = attachment.segment.outwardNormal;
  if (!attachment.attached) {
    return { point: center, normal };
  }
  const insetFt = attachment.signedDistanceFt - flushDistanceFt;
  return { point: moveInward(center, normal, insetFt), normal };
}
